/**
 * Google Tag Manager dataLayer push helpers for the job application funnel and auth events.
 */

import type { JobDetail, JobSummary } from '@/services/jobs/model';

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

export function pushDataLayer(event: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
}

export type GtmJobFields = {
  job_id: string;
  job_title: string;
  company_name: string;
  job_location: string;
};

export function getGtmJobFields(job: JobSummary | JobDetail): GtmJobFields {
  return {
    job_id: job.id,
    job_title: job.jobTitle,
    company_name: job.company.name,
    job_location: job.workingLocations.join(', '),
  };
}

export type GtmApplyButtonLocation = 'job_list' | 'job_detail';

export function pushApplyStart(
  job: JobSummary | JobDetail,
  buttonLocation: GtmApplyButtonLocation
): void {
  pushDataLayer({
    event: 'js_apply_start',
    ...getGtmJobFields(job),
    button_location: buttonLocation,
  });
}

export function pushAgreePrivacyPolicy(job: JobSummary | JobDetail): void {
  pushDataLayer({
    event: 'js_agree_privacy_policy',
    ...getGtmJobFields(job),
    agree_status: true,
  });
}

export function pushSubmitApplication(job: JobSummary | JobDetail): void {
  pushDataLayer({
    event: 'js_submit_application',
    ...getGtmJobFields(job),
    form_step: 'application_form',
  });
}

export function pushApplyOpportunity(job: JobSummary | JobDetail): void {
  pushDataLayer({
    event: 'js_apply_opportunity',
    ...getGtmJobFields(job),
    form_step: 'confirmation',
  });
}

export type GtmAuthMethod = 'email' | 'google';

// The app has no paid plans yet, so every new account starts on the free plan.
export const GTM_DEFAULT_PLAN = 'free';

export function pushLoginSuccess(userId: string, method: GtmAuthMethod): void {
  pushDataLayer({
    event: 'login_success',
    user_id: userId,
    method,
  });
}

export function pushSignupSuccess(
  userId: string,
  method: GtmAuthMethod,
  plan: string = GTM_DEFAULT_PLAN
): void {
  pushDataLayer({
    event: 'signup_success',
    user_id: userId,
    method,
    plan,
  });
}
